/** Hugging Face 集成配置；复用集成弹窗，Token 不回显且地址不变时留空保留。 */
import {
  HUGGINGFACE_ROUTER_BASE_URL,
  type HuggingFaceCredentialResponse,
  UpsertHuggingFaceCredentialRequestSchema,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useHuggingFaceCredential } from '~/data/queries';
import { huggingFaceApi } from '~/hooks/use-huggingface';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';
import { ProviderConcurrencyField } from './ProviderConcurrencyField';

function ConfigForm({ initial }: { initial: HuggingFaceCredentialResponse }) {
  const [baseUrl, setBaseUrl] = useState(initial.base_url ?? HUGGINGFACE_ROUTER_BASE_URL);
  const [apiKey, setApiKey] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(String(initial.timeout_seconds ?? 60));
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const payload = () => ({
    base_url: baseUrl.trim(),
    ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
    timeout_seconds: Number(timeoutSeconds),
  });
  // Token 只作为瞬时请求参数；mutationFn 不接收变量，避免进入 mutation cache。
  const save = useMutation({
    mutationFn: async () =>
      huggingFaceApi.upsertCredential(UpsertHuggingFaceCredentialRequestSchema.parse(payload())),
    onSuccess: async () => {
      setApiKey('');
      await qc.invalidateQueries({ queryKey: ['integrations', 'huggingface'] });
      toast('Hugging Face 配置已保存', { tone: 'pos' });
    },
    onError: (e) => setError(e.message),
  });
  const submit = () => {
    const result = UpsertHuggingFaceCredentialRequestSchema.safeParse(payload());
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? '配置错误');
      return;
    }
    setError('');
    save.mutate();
  };
  return (
    <>
      <FormRow
        label="Base URL"
        required
        hint="默认 Inference Providers 路由；使用专用推理端点时填写端点地址，以 /v1 结尾"
      >
        <input
          aria-label="Hugging Face Base URL"
          className="input mono"
          value={baseUrl}
          placeholder={HUGGINGFACE_ROUTER_BASE_URL}
          disabled={save.isPending}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </FormRow>
      <FormRow
        label="Access Token"
        required={!initial.configured}
        hint={
          initial.configured
            ? '地址不变时可留空保留；修改 Base URL 须重新填写 Token'
            : '首次配置必须填写；建议用细粒度 Token：路由地址勾选「Make calls to Inference Providers」，专用端点勾选对应端点的 Inference Endpoints 调用权限'
        }
      >
        <PasswordInput
          aria-label="Hugging Face Token"
          className="input mono"
          value={apiKey}
          disabled={save.isPending}
          onChange={(e) => setApiKey(e.target.value)}
          autoComplete="new-password"
        />
      </FormRow>
      <FormRow label="请求超时（秒）" required hint="30–120 秒；长文本或单卡专用端点可调高">
        <input
          aria-label="Hugging Face 请求超时"
          className="input mono"
          type="number"
          min={30}
          max={120}
          step={1}
          value={timeoutSeconds}
          disabled={save.isPending}
          onChange={(e) => setTimeoutSeconds(e.target.value)}
        />
      </FormRow>
      <p className="muted">
        Token 加密存储，明文不会回显。保存和测试连接只校验
        Token，不调用模型。模型与提示词在「大模型配置」中分别设置。
      </p>
      <p className="muted">
        正文会发送给 Hugging Face
        及所选推理服务商；按用量计费，账户须有可用额度。专用端点开启缩到零时，首次调用需冷启动数分钟，建议保留最少副本或先预热。
      </p>
      {error && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {error}
        </div>
      )}
      <button
        className="btn btn-accent"
        disabled={save.isPending || (!initial.configured && apiKey.trim().length < 8)}
        onClick={submit}
      >
        {save.isPending ? '保存中…' : '保存配置'}
      </button>
    </>
  );
}

export function HuggingFaceConfigDialog({ onClose }: { onClose: () => void }) {
  const q = useHuggingFaceCredential();
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const test = useMutation({
    mutationFn: huggingFaceApi.testConnection,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'huggingface'] });
      toast('Hugging Face Token 验证通过', { tone: 'pos' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  const remove = useMutation({
    mutationFn: huggingFaceApi.removeCredential,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'huggingface'] });
      setRemoving(false);
      toast('Hugging Face 配置已移除', { tone: 'warn' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  return (
    <Dialog
      title="Hugging Face 集成配置"
      subtitle="Inference Providers 路由或专用推理端点 · Access Token"
      icon="IconBranch"
      width={620}
      onClose={onClose}
      closeOnEsc={!removing}
      footer={
        <button className="btn" onClick={onClose}>
          完成
        </button>
      }
    >
      <div style={{ padding: 18 }}>
        {q.isPending ? (
          <p>加载配置…</p>
        ) : q.isError ? (
          <div role="alert">{q.error.message}</div>
        ) : (
          <>
            <p>
              {q.data.configured
                ? `已配置 · Token 尾号 ${q.data.key_tail}`
                : '尚未配置 Hugging Face'}
            </p>
            <ConfigForm key={q.data.updated_at ?? 'unset'} initial={q.data} />
            {q.data.configured && (
              <div className="hstack" style={{ gap: 8, marginTop: 16 }}>
                <button
                  className="btn"
                  disabled={test.isPending || remove.isPending}
                  onClick={() => test.mutate()}
                >
                  {test.isPending ? '测试中…' : '测试连接'}
                </button>
                <button
                  className="btn btn-danger"
                  disabled={remove.isPending || test.isPending}
                  onClick={() => setRemoving(true)}
                >
                  移除配置
                </button>
              </div>
            )}
          </>
        )}
        <ProviderConcurrencyField provider="huggingface" label="Hugging Face" />
      </div>
      {removing && (
        <ConfirmDialog
          title="移除 Hugging Face 配置"
          message="移除后使用 Hugging Face 的审核与脱敏将无法执行；既有任务产物不受影响。"
          requireTyping="Hugging Face"
          tone="danger"
          onConfirm={() => remove.mutate()}
          onClose={() => setRemoving(false)}
        />
      )}
    </Dialog>
  );
}
